// src/utils/categories.ts
//
// Jeden číselník kategorií VÝSLEDKŮ (results_entries.category) – krok 7
// redesignu, backlog #8 a #10.
//
// V results_entries.category se dnes potkávají čtyři zápisy téže věci:
//   - publishResults.ts (publikace z live scoringu): do 4. 10. 2026 "člen",
//     "člen-junior", "člen-žena", "člen-senior", "člen-veterán", "nečlen";
//     od té doby popisek kategorie z tohoto číselníku ("Muži", "Ženy"…,
//     "" = neznámá) – členství se nezveřejňuje (rozhodnutí majitele)
//   - AdminLiveResults / DisplayView (jen zobrazení, nezapisují): "Muž",
//     "Žena", "Junior", "Senior", "Veterán", "Nováček"
//   - původní filtr UserResults: "Muži", "Ženy", "Junior", "Senior",
//     "Veterán", "Nováček" (a tak je zapisoval i ruční XLSX import)
//   - PractiScore / XLSX import: volný text sloupce Category (nebo Class)
// Tenhle modul všechny legacy hodnoty mapuje PŘI ČTENÍ na jedno id
// (normalizeCategoryId). Od kroku 9 zapisuje publikace z live scoringu
// vedle textu `category` i pole `categoryId` (id z tohoto číselníku; null =
// nezařazeno) – mimo jiné jediné místo, kde je vidět nováček. Čtecí strana
// proto bere kategorii záznamu přes resolveEntryCategoryId(entry): platné
// categoryId má přednost, jinak se mapuje legacy text. Modul sám nic
// nezapisuje.
//
// Druhá část je jediný zdroj věkových hranic kategorií (defaults/categories):
// DEFAULT_CATEGORY_DEFAULTS / DEFAULT_CATEGORY_LIMITS + loadCategoryDefaults()
// + useCategoryDefaults(). utils/getUserCategory.ts (profilová / cenová
// kategorie člena) zůstává beze změny – řeší jinou věc (členství + cena).
//
// Politika chyb: loadCategoryDefaults() nikdy neselže – při chybě čtení
// zaloguje reportWarning a vrátí poslední známé defaults, jinak
// DEFAULT_CATEGORY_DEFAULTS (výsledky se s výchozími hranicemi zobrazit dají).
// Cenová kategorie v utils/getUserCategory.ts (fetchJuniorMaxAge) naopak chybu
// propaguje (reject) – bez hranice se cena určit nedá a obrazovka má nabídnout
// „Zkusit znovu". Ten rozdíl je záměrný, nesjednocovat.
//
// Firestore část (loadCategoryDefaults, clearCategoryDefaultsCache,
// useCategoryDefaults + cache relace) se může později oddělit do
// utils/categoryDefaults.ts. Zatím ne – importují ji souběžně rozpracované
// obrazovky kroku 8 (AdminRegistrations, MembersList, …); přesun až po jejich
// dokončení, jinak by se měnily cesty importů pod rukama.

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { reportWarning } from "./reportError";

// ─────────────────────────────────────────────────────────────
//  Číselník kategorií výsledků
// ─────────────────────────────────────────────────────────────

/** Id kategorie výsledků – bez diakritiky, vhodné do URL i jako klíč. */
export type ResultCategoryId =
  | "muzi"
  | "zeny"
  | "juniori"
  | "seniori"
  | "veterani"
  | "novacci";

export interface ResultCategory {
  id: ResultCategoryId;
  /** Popisek do filtru a tabulek. */
  label: string;
  /** Zkratka do úzkých míst (mobilní karty, štítky). */
  short: string;
  /** Pořadí ve filtru a při řazení podle kategorie (menší = dřív). */
  order: number;
}

/**
 * Kategorie v pořadí pro filtr i řazení. Pořadí odpovídá původnímu filtru
 * výsledků (Muži, Ženy, Junior, Senior, Veterán, Nováček); live tabule řadí
 * jinak (Junior první) – to se sjednotí v kroku 13 (liveCategory.ts).
 */
export const RESULT_CATEGORIES: ResultCategory[] = [
  { id: "muzi", label: "Muži", short: "M", order: 1 },
  { id: "zeny", label: "Ženy", short: "Ž", order: 2 },
  { id: "juniori", label: "Junioři", short: "J", order: 3 },
  { id: "seniori", label: "Senioři", short: "S", order: 4 },
  { id: "veterani", label: "Veteráni", short: "V", order: 5 },
  { id: "novacci", label: "Nováčci", short: "N", order: 6 },
];

export const RESULT_CATEGORY_BY_ID: Record<ResultCategoryId, ResultCategory> =
  RESULT_CATEGORIES.reduce(
    (acc, c) => ({ ...acc, [c.id]: c }),
    {} as Record<ResultCategoryId, ResultCategory>
  );

/** Popisek pro záznam, který se nepodařilo zařadit (nečlen bez věku, prázdná hodnota, cizí text). */
export const UNKNOWN_CATEGORY_LABEL = "Nezařazeno";

/** Pořadí nezařazených záznamů – vždy až za známými kategoriemi. */
export const UNKNOWN_CATEGORY_ORDER = 99;

export function isResultCategoryId(value: unknown): value is ResultCategoryId {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(RESULT_CATEGORY_BY_ID, value)
  );
}

/**
 * Text bez diakritiky, malými písmeny, interpunkce a pomlčky nahrazené
 * jednou mezerou ("Člen-Žena " → "clen zena").
 */
const fold = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Přesné (už složené) tvary → id. Klíče jsou výstupy fold(), takže
 * "člen-žena", "Člen – žena" i "CLEN ZENA" sedí na "clen zena".
 */
const EXACT: Record<string, ResultCategoryId> = {
  // muži – "člen" je větev publishResults pro muže 17–49 (do kroku 9 i záložní
  // hodnota při nečitelném profilu – od kroku 9 tam publikace píše "nečlen")
  muz: "muzi",
  muzi: "muzi",
  men: "muzi",
  man: "muzi",
  male: "muzi",
  clen: "muzi",
  "clen muz": "muzi",
  "clen muzi": "muzi",
  // ženy – klubové pravidlo nemá pásmo seniorek/veteránek: žena nad věkovou
  // hranicí juniora je vždy "žena" (publishResults, AdminLiveResults,
  // DisplayView), proto i seniorka/veteránka patří sem
  zena: "zeny",
  zeny: "zeny",
  woman: "zeny",
  women: "zeny",
  lady: "zeny",
  ladies: "zeny",
  female: "zeny",
  clenka: "zeny",
  "clen zena": "zeny",
  "clen zeny": "zeny",
  dama: "zeny",
  damy: "zeny",
  seniorka: "zeny",
  seniorky: "zeny",
  veteranka: "zeny",
  veteranky: "zeny",
  // junioři (juniorka je junior – věk má přednost před pohlavím)
  junior: "juniori",
  juniori: "juniori",
  juniors: "juniori",
  juniorka: "juniori",
  juniorky: "juniori",
  "clen junior": "juniori",
  jun: "juniori",
  jr: "juniori",
  // senioři (muži 50–59)
  senior: "seniori",
  seniori: "seniori",
  seniors: "seniori",
  "clen senior": "seniori",
  sen: "seniori",
  sr: "seniori",
  // veteráni (muži 60+; IPSC „Super Senior" = 60+, tedy náš veterán)
  veteran: "veterani",
  veterani: "veterani",
  veterans: "veterani",
  "clen veteran": "veterani",
  "super senior": "veterani",
  supersenior: "veterani",
  vet: "veterani",
  // nováčci
  novacek: "novacci",
  novacci: "novacci",
  novacka: "novacci",
  novacky: "novacci",
  newbie: "novacci",
  newbies: "novacci",
  rookie: "novacci",
  rookies: "novacci",
  beginner: "novacci",
  zacatecnik: "novacci",
  zacatecnici: "novacci",
};

/**
 * Klíčová slova pro volný text importu ("Muži – A", "Senior 50+", "Lady
 * Senior"). Pořadí = priorita a kopíruje klubové pravidlo z publishResults,
 * AdminLiveResults a DisplayView: junior → žena → muž/senior/veterán podle
 * věku. Přednost před ženami má jen nováček a junior (juniorka patří
 * k juniorům – stejně jako při publikaci); pásmo seniorek ani veteránek
 * neexistuje, takže "Lady Senior", "Senior ženy", "Žena senior" i "Seniorka"
 * jsou ženy. Dál veterán před seniorem ("super senior") a ženy před muži
 * ("women" obsahuje "men", "female" obsahuje "male").
 */
const KEYWORDS: Array<[RegExp, ResultCategoryId]> = [
  [/novac|newbie|rookie|beginner|zacatec/, "novacci"],
  [/junior/, "juniori"],
  [
    /\bzen[aey]?\b|zensk|lad(y|ies)|wom[ae]n|female|\bdam[ay]\b|seniork|veterank/,
    "zeny",
  ],
  [/veteran|super ?senior/, "veterani"],
  [/senior/, "seniori"],
  [/\bmuz(i|u)?\b|muzsk|\bm[ae]n\b|\bmale\b/, "muzi"],
];

/**
 * Převod čehokoli, co je dnes v results_entries.category, na id kategorie.
 * Nerozhoduje velikost písmen, diakritika ani oddělovače.
 *
 * Vrací null = nezařazeno pro:
 *  - "nečlen": je to CENOVÁ kategorie (publikace ji do 4. 10. 2026 psala
 *    každému bez zaplaceného členství bez ohledu na věk a pohlaví) – do výsledkové
 *    kategorie se bez věku/pohlaví zařadit nedá, hádat „muži" by bylo špatně
 *    (publikace od kroku 9 zařazení podle profilu nese v categoryId – viz
 *    resolveEntryCategoryId);
 *  - prázdnou hodnotu, "Neznámá", nečíselné/cizí texty a jednopísmenné kódy
 *    (sloupec Class z PractiScore má "A", "B", "M", "U"… – "M" není muž).
 */
export function normalizeCategoryId(raw: unknown): ResultCategoryId | null {
  if (typeof raw !== "string") return null;
  const key = fold(raw);
  if (!key) return null;
  if (isResultCategoryId(key)) return key;
  const exact = EXACT[key];
  if (exact) return exact;
  for (let i = 0; i < KEYWORDS.length; i++) {
    if (KEYWORDS[i][0].test(key)) return KEYWORDS[i][1];
  }
  return null;
}

/** Popisek známé kategorie; neznámé/null id → UNKNOWN_CATEGORY_LABEL. */
export function categoryLabel(id: string | null | undefined): string {
  return isResultCategoryId(id)
    ? RESULT_CATEGORY_BY_ID[id].label
    : UNKNOWN_CATEGORY_LABEL;
}

/** Zkratka známé kategorie; neznámé/null id → "–". */
export function categoryShort(id: string | null | undefined): string {
  return isResultCategoryId(id) ? RESULT_CATEGORY_BY_ID[id].short : "–";
}

/** Pořadí pro řazení podle kategorie; neznámé/null id → UNKNOWN_CATEGORY_ORDER (na konec). */
export function categoryOrder(id: string | null | undefined): number {
  return isResultCategoryId(id)
    ? RESULT_CATEGORY_BY_ID[id].order
    : UNKNOWN_CATEGORY_ORDER;
}

/**
 * Text do buňky tabulky z uložené (legacy) hodnoty: popisek kategorie, když
 * ji umíme zařadit; jinak původní text (ať import s cizím označením něco
 * ukáže); prázdná hodnota → "".
 */
export function displayCategory(raw: unknown): string {
  const id = normalizeCategoryId(raw);
  if (id) return RESULT_CATEGORY_BY_ID[id].label;
  return typeof raw === "string" ? raw.trim() : "";
}

/** Pole záznamu výsledků (results_entries), ze kterých se určuje kategorie. */
export interface EntryCategorySource {
  /** Id z číselníku – zapisuje publikace od kroku 9; null = nezařazeno. */
  categoryId?: unknown;
  /** Legacy text kategorie (publikace, import, starší data). */
  category?: unknown;
}

/**
 * Kategorie záznamu výsledků: platné `categoryId` (publikace od kroku 9 –
 * jen tak se pozná nováček) má přednost; jinak (starší záznam, import,
 * null, neznámá hodnota) normalizeCategoryId(category).
 */
export function resolveEntryCategoryId(
  entry: EntryCategorySource | null | undefined
): ResultCategoryId | null {
  if (!entry) return null;
  if (isResultCategoryId(entry.categoryId)) return entry.categoryId;
  return normalizeCategoryId(entry.category);
}

/**
 * displayCategory pro celý záznam: popisek kategorie z resolveEntryCategoryId,
 * jinak uložený text (nečlen, cizí označení importu), jinak "".
 */
export function displayEntryCategory(
  entry: EntryCategorySource | null | undefined
): string {
  const id = resolveEntryCategoryId(entry);
  if (id) return RESULT_CATEGORY_BY_ID[id].label;
  return entry && typeof entry.category === "string" ? entry.category.trim() : "";
}

// ─────────────────────────────────────────────────────────────
//  Věkové hranice – dokument defaults/categories
// ─────────────────────────────────────────────────────────────

/** Jedno věkové pásmo v dokumentu defaults/categories (edituje AdminOverview). */
export interface CategoryAgeBand {
  label: string;
  minAge: number;
  /** null = bez horní hranice. */
  maxAge: number | null;
}

export type CategoryBandKey = "junior" | "woman" | "man" | "senior" | "veteran";

/** Tvar dokumentu defaults/categories – NEMĚNIT (krok 9: „tvar defaults/* dokumentů"). */
export type CategoryDefaults = Record<CategoryBandKey, CategoryAgeBand>;

/**
 * Výchozí pásma, když dokument chybí nebo je neúplný. Shodné s editorem
 * v AdminOverview a publishResults (junior ≤ 16, muži ≤ 49, senioři ≤ 59);
 * AdminRegistrations/MembersList se na tyto hodnoty sjednocují v kroku 8 (#10).
 */
export const DEFAULT_CATEGORY_DEFAULTS: CategoryDefaults = {
  junior: { label: "Junioři", minAge: 0, maxAge: 16 },
  woman: { label: "Ženy", minAge: 17, maxAge: null },
  man: { label: "Muži", minAge: 17, maxAge: 49 },
  senior: { label: "Senioři", minAge: 50, maxAge: 59 },
  veteran: { label: "Veteráni", minAge: 60, maxAge: null },
};

/** Tři hranice, se kterými obrazovky reálně počítají. */
export interface CategoryLimits {
  /** Nejvyšší věk juniora (věk = rok závodu − rok narození). */
  juniorMax: number;
  /** Nejnižší věk seniora. */
  seniorMin: number;
  /** Nejnižší věk veterána. */
  veteranMin: number;
}

export const DEFAULT_CATEGORY_LIMITS: CategoryLimits = {
  juniorMax: 16,
  seniorMin: 50,
  veteranMin: 60,
};

const finiteNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const bandOf = (source: unknown, key: CategoryBandKey): CategoryAgeBand => {
  const fallback = DEFAULT_CATEGORY_DEFAULTS[key];
  const raw =
    source && typeof source === "object"
      ? (source as Record<string, unknown>)[key]
      : undefined;
  if (!raw || typeof raw !== "object") return { ...fallback };
  const band = raw as Record<string, unknown>;
  const maxAge =
    band.maxAge === null ? null : finiteNumber(band.maxAge) ?? fallback.maxAge;
  return {
    label: typeof band.label === "string" && band.label ? band.label : fallback.label,
    minAge: finiteNumber(band.minAge) ?? fallback.minAge,
    maxAge,
  };
};

/**
 * Úplné defaults z libovolných dat dokumentu (chybějící / nečíselné hodnoty
 * doplní z DEFAULT_CATEGORY_DEFAULTS po jednotlivých polích).
 */
export function toCategoryDefaults(source: unknown): CategoryDefaults {
  return {
    junior: bandOf(source, "junior"),
    woman: bandOf(source, "woman"),
    man: bandOf(source, "man"),
    senior: bandOf(source, "senior"),
    veteran: bandOf(source, "veteran"),
  };
}

/** Číselné pole pásma přímo ze surových dat dokumentu (bez doplnění defaults). */
const rawBandNumber = (
  source: unknown,
  key: CategoryBandKey,
  field: "minAge" | "maxAge"
): number | null => {
  if (!source || typeof source !== "object") return null;
  const band = (source as Record<string, unknown>)[key];
  if (!band || typeof band !== "object") return null;
  return finiteNumber((band as Record<string, unknown>)[field]);
};

/**
 * Hranice z dat dokumentu defaults/categories (nebo z CategoryDefaults):
 * junior.maxAge, senior.minAge (náhradně man.maxAge + 1), veteran.minAge
 * (náhradně senior.maxAge + 1) – AdminRegistrations četl minAge, publishResults
 * maxAge; tady se obě poloviny sejdou. Chybějící hodnoty → DEFAULT_CATEGORY_LIMITS.
 */
export function categoryLimitsOf(source: unknown): CategoryLimits {
  const manMax = rawBandNumber(source, "man", "maxAge");
  const seniorMax = rawBandNumber(source, "senior", "maxAge");
  return {
    juniorMax:
      rawBandNumber(source, "junior", "maxAge") ?? DEFAULT_CATEGORY_LIMITS.juniorMax,
    seniorMin:
      rawBandNumber(source, "senior", "minAge") ??
      (manMax !== null ? manMax + 1 : DEFAULT_CATEGORY_LIMITS.seniorMin),
    veteranMin:
      rawBandNumber(source, "veteran", "minAge") ??
      (seniorMax !== null ? seniorMax + 1 : DEFAULT_CATEGORY_LIMITS.veteranMin),
  };
}

/** Poslední úspěšně načtené defaults; undefined = v této relaci ještě nenačteno. */
let cachedDefaults: CategoryDefaults | undefined;
/** Rozběhnuté čtení – souběžná volání sdílejí jeden požadavek. */
let pendingDefaults: Promise<CategoryDefaults> | null = null;

/**
 * Načte defaults/categories (jednou za relaci; `force` čte znovu, např. po
 * uložení v administraci). Nikdy nevyhazuje: při chybě čtení zaloguje
 * reportWarning a vrátí poslední známé defaults, jinak DEFAULT_CATEGORY_DEFAULTS
 * – stejně jako dosud publishResults a fetchJuniorMaxAge.
 */
export function loadCategoryDefaults(force = false): Promise<CategoryDefaults> {
  if (!force && cachedDefaults) return Promise.resolve(cachedDefaults);
  if (pendingDefaults) return pendingDefaults;
  const run = getDoc(doc(db, "defaults", "categories"))
    .then((snap) => {
      const value = toCategoryDefaults(snap.exists() ? snap.data() : undefined);
      cachedDefaults = value;
      return value;
    })
    .catch((err: unknown) => {
      reportWarning("loadCategoryDefaults", err);
      return cachedDefaults ?? DEFAULT_CATEGORY_DEFAULTS;
    })
    .finally(() => {
      if (pendingDefaults === run) pendingDefaults = null;
    });
  pendingDefaults = run;
  return run;
}

/** Zapomene načtené defaults (po uložení dokumentu v administraci). */
export function clearCategoryDefaultsCache(): void {
  cachedDefaults = undefined;
}

export interface CategoryDefaultsState {
  defaults: CategoryDefaults;
  limits: CategoryLimits;
  /** True po dokončení prvního pokusu o načtení (i neúspěšného), případně hned z cache relace. */
  ready: boolean;
}

/**
 * Hook pro obrazovky (backlog #10 – jeden zdroj hranic). Startuje z hodnoty
 * načtené dřív v relaci a při mountu ji načte znovu, kdyby ji admin změnil.
 * Do načtení platí DEFAULT_CATEGORY_* a ready = false.
 */
export function useCategoryDefaults(): CategoryDefaultsState {
  const [state, setState] = useState<CategoryDefaultsState>(() => ({
    defaults: cachedDefaults ?? DEFAULT_CATEGORY_DEFAULTS,
    limits: cachedDefaults ? categoryLimitsOf(cachedDefaults) : DEFAULT_CATEGORY_LIMITS,
    ready: cachedDefaults !== undefined,
  }));

  useEffect(() => {
    let alive = true;
    loadCategoryDefaults(true).then((defaults) => {
      if (alive) {
        setState({ defaults, limits: categoryLimitsOf(defaults), ready: true });
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}
